import { AttrParts, ConceptType } from "./attrCalculator";

export interface Rule {
  id: ConceptType;
  test: (words: AttrParts[]) => boolean;
  apply: (words: AttrParts[]) => RuleResult;
}

export interface RuleResult {
  words: AttrParts[];
  rule: ConceptType;
}

// Cuando el concepto tiene una palabra y termina en ese, poner las 4 primeras letras de la palabra y agregar ese al final.
const OneWordEndS: Rule = {
  id: "OneWordEndS",
  test: (words: AttrParts[]) =>
    words.length === 1 && words[0].current[words[0].current.length - 1] === "s",
  apply: (words: AttrParts[]) => {
    words[0].current = words[0].current.slice(0, 4) + "s";
    return { words, rule: "OneWordEndS" };
  },
};

// Cuando el concepto tiene 2 palabras que terminan en ese, poner las 4 primeras letras de la primera palabra, las 4 primeras letras de la segunda y agregar ese al final.
const TwoWordsEndS: Rule = {
  id: "TwoWordsEndS",
  test: (words: AttrParts[]) =>
    words.length === 2 && words[1].current[words[1].current.length - 1] === "s",
  apply: (words: AttrParts[]) => {
    words[0].current = words[0].current.slice(0, 4);
    words[1].current = words[1].current.slice(0, 4) + "s";
    return { words, rule: "TwoWordsEndS" };
  },
};

// Cuando el concepto tiene 1 o 2 palabras se utiliza el truncamiento con las primeras 4 letras de las palabras
const OneOrTwoWords: Rule = {
  id: "OneOrTwoWords",
  test: (words: AttrParts[]) =>
    (words.length === 1 &&
      words[0].current[words[0].current.length - 1] !== "s") ||
    (words.length === 2 &&
      words[1].current[words[1].current.length - 1] !== "s"),
  apply: (words: AttrParts[]) => {
    words[0].current = words[0].current.slice(0, 4);
    if (words[1]) words[1].current = words[1].current.slice(0, 4);
    return { words, rule: "OneOrTwoWords" };
  },
};

// Cuando el concepto tiene 3 palabras hasta 10, utilizar el criterio de truncamiento para las fórmulas fijas, esto es, se toma la inicial de cada palabra.
const ThreeOrMoreWords: Rule = {
  id: "ThreeOrMoreWords",
  test: (words: AttrParts[]) => words.length >= 3,
  apply: (words: AttrParts[]) => {
    words.forEach(
      (part: AttrParts) => (part.current = part.current.slice(0, 1))
    );
    return { words, rule: "ThreeOrMoreWords" };
  },
};

// Cuando el concepto tiene 1 o 2 términos en los que se incluye una cadena de caracteres numéricos de 4 cifras, poner las 2 primeras letras de la primera palabra, las 2 primeras letras de la segunda y la cadena numérica.
const OneOrTwooWords4Num: Rule = {
  id: "OneOrTwooWords4Num",
  test: (words: AttrParts[]) =>
    words.length <= 3 &&
    /\d{4}/.test(words[words.length - 1].current) &&
    !/\d{4}/.test(words[words.length - 2].current),
  apply: (words: AttrParts[]) => {
    words.forEach((part: AttrParts, idx: number) => {
      if (idx !== words.length - 1) part.current = part.current.slice(0, 2);
    });
    return { words, rule: "OneOrTwooWords4Num" };
  },
};

// Cuando el concepto tiene más de 2 palabras y se incluye una cadena de caracteres numéricos de 4 cifras, se toma la inicial de cada palabra y la cadena numérica completa.
const ThreeOrMoreWords4Num: Rule = {
  id: "ThreeOrMoreWords4Num",
  test: (words: AttrParts[]) =>
    words.length > 3 &&
    /\d{4}/.test(words[words.length - 1].current) &&
    !/\d{4}/.test(words[words.length - 2].current),
  apply: (words: AttrParts[]) => {
    words.forEach((part: AttrParts, idx: number) => {
      if (idx !== words.length - 1) part.current = part.current.slice(0, 1);
    });
    return { words, rule: "ThreeOrMoreWords4Num" };
  },
};

// Cuando el concepto tiene 1 o 2 términos en los que se incluyen dos cadenas de caracteres numéricos separadas por un guion, poner las 2 primeras letras de la primera palabra, las 2 primeras letras de la segunda y la segunda cadena numérica.
const OneOrTwooWords8Num: Rule = {
  id: "OneOrTwooWords8Num",
  test: (words: AttrParts[]) =>
    words.length <= 4 &&
    /\d{4}/.test(words[words.length - 1].current) &&
    /\d{4}/.test(words[words.length - 2].current),
  apply: (words: AttrParts[]) => {
    words.forEach((part: AttrParts, idx: number) => {
      if (idx < words.length - 1) part.current = part.current.slice(0, 2);
    });
    words[words.length - 2].current = "";
    return { words, rule: "OneOrTwooWords8Num" };
  },
};

// Cuando el concepto tiene más de 2 palabras y se incluyen dos cadenas de caracteres numéricos separadas por un guion, se toma la inicial de cada palabra y la segunda cadena numérica completa.
const ThreeOrMoreWords8Num: Rule = {
  id: "ThreeOrMoreWords8Num",
  test: (words: AttrParts[]) =>
    words.length > 4 &&
    /\d{4}/.test(words[words.length - 1].current) &&
    /\d{4}/.test(words[words.length - 2].current),
  apply: (words: AttrParts[]) => {
    words.forEach((part: AttrParts, idx: number) => {
      if (idx !== words.length - 1) part.current = part.current.slice(0, 1);
    });
    words[words.length - 2].current = "";
    return { words, rule: "ThreeOrMoreWords8Num" };
  },
};

const rules = {
  OneWordEndS,
  TwoWordsEndS,
  OneOrTwoWords,
  OneOrTwooWords4Num,
  ThreeOrMoreWords4Num,
  OneOrTwooWords8Num,
  ThreeOrMoreWords8Num,
};

const ruleDescriptions = {
  OneOrTwoWords: {
    id: 1,
    title: `Cuando el concepto tiene 1 o 2 palabras se utiliza el truncamiento con las primeras 4 letras por palabra.`,
  },
  OneWordEndS: {
    id: 2,
    title: `Cuando el concepto tiene una palabra y termina en "s", se debe poner las 4 primeras letras de la palabra y agregar "s" al final.`,
  },
  TwoWordsEndS: {
    id: 3,
    title: `Cuando el concepto tiene 2 palabras que terminan en "s", se debe poner las 4 primeras letras de la primera palabra, las 4 primeras letras de la segunda y agregar "s" al final.`,
  },
  ThreeOrMoreWords: {
    id: 4,
    title: `Cuando el concepto tiene 3 palabras hasta 10, utilizar el criterio de truncamiento para las fórmulas fijas, esto es, se toma la inicial de cada palabra.`,
  },
  OneOrTwooWords4Num: {
    id: 5,
    title: `Cuando el concepto tiene 1 o 2 términos en los que se incluye una cadena de caracteres numéricos de 4 cifras, se debe poner las 2 primeras letras de la primera palabra, las 2 primeras letras de la segunda y la cadena numérica.`,
  },
  ThreeOrMoreWords4Num: {
    id: 6,
    title: `Cuando el concepto tiene más de 2 palabras y se incluye una cadena de caracteres numéricos de 4 cifras, se toma la inicial de cada palabra y la cadena numérica completa.`,
  },
  OneOrTwooWords8Num: {
    id: 7,
    title: `Cuando el concepto tiene 1 o 2 términos en los que se incluyen dos cadenas de caracteres numéricos separadas por un guion, se debe poner las 2 primeras letras de la primera palabra, las 2 primeras letras de la segunda y la segunda cadena numérica.`,
  },
  ThreeOrMoreWords8Num: {
    id: 8,
    title: `Cuando el concepto tiene más de 2 palabras y se incluyen dos cadenas de caracteres numéricos separadas por un guion, se toma la inicial de cada palabra y la segunda cadena numérica completa.`,
  },
};

const defaultRule = ThreeOrMoreWords;

export { rules, defaultRule, ruleDescriptions };
